import styled from "@emotion/styled";
import { ArrowDownward, ArrowUpward, Check } from "@mui/icons-material";
import { Box, Chip, Input, Link, Stack, Table as MuiTable, Typography } from "@mui/joy";
import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  Header as HeaderType,
  useReactTable,
  flexRender,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { ApiChallengeEntry } from "griptonite-api-client";
import { useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useChallengeEntries, useChallengeQuery } from "@/hooks/challenges";

import Avatar from "../Avatar";

const StyledTh = styled.th`
  &.active {
    background: #e5efff;
    position: relative;
    border: 1px solid #0084ff;
    border-bottom-width: 1px !important;
  }
`;

const columnHelper = createColumnHelper<ApiChallengeEntry>();

const TableHeader = ({ header }: { header: HeaderType<ApiChallengeEntry, unknown> }) => {
  const isSorted = header.column.getIsSorted();
  return (
    <StyledTh>
      <Link
        underline="none"
        textColor={header.column.getIsSorted() ? "primary.500" : "neutral.800"}
        onClick={header.column.getToggleSortingHandler()}
        startDecorator={isSorted ? isSorted === "desc" ? <ArrowDownward /> : <ArrowUpward /> : null}
        sx={{ flex: 1 }}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </Link>
    </StyledTh>
  );
};

const UserSearch = ({
  searchQuery,
  setSearchQuery,
}: {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}) => {
  const [query, setQuery] = useState(searchQuery);
  const debouncedUpdate = useDebouncedCallback((q) => {
    setSearchQuery(q);
  }, 150);

  return (
    <Input
      placeholder="Search users..."
      value={query}
      onChange={(e) => {
        setQuery(e.target.value);
        if (e.target.value === "") {
          setSearchQuery("");
        } else {
          debouncedUpdate(e.target.value);
        }
      }}
    />
  );
};

const FiltersBar = ({
  searchQuery,
  setSearchQuery,
}: {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}) => {
  return (
    <Stack
      direction="row"
      sx={{
        padding: 1,
        gap: 1,
        borderBottom: "1px solid",
        borderColor: "neutral.200",
      }}
    >
      <UserSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
    </Stack>
  );
};

export default function ChallengeEntries({ challengeUuid }: { challengeUuid: string }) {
  const { data: challenge } = useChallengeQuery(challengeUuid);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: entries } = useChallengeEntries(challengeUuid);

  const filteredEntries = useMemo(() => {
    if (!entries) return [];
    return entries.filter((entry) => {
      const searchMatches =
        !searchQuery || entry.user.name.toLowerCase().includes(searchQuery.toLowerCase());
      return searchMatches;
    });
  }, [entries, searchQuery]);

  const navigate = useNavigate();
  const { entryUuid: activeEntryUuid } = useParams({ strict: false });

  const onRowClick = (entryUuid: string) => {
    navigate({
      from: "/gym/$venueUuid/challenges/$challengeUuid/entries",
      to: "./$entryUuid",
      params: (prev) => ({ ...prev, entryUuid }),
      replace: true,
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("user.name", {
        header: "User",
        id: "user",
        cell: (props) => (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Avatar user={props.row.original.user} size={32} />
            <Typography fontWeight="bold">{props.getValue()}</Typography>
          </Stack>
        ),
      }),
      columnHelper.accessor("value", {
        header: "Visits",
        cell: (props) => props.getValue(),
      }),
      columnHelper.accessor("creationTime", {
        header: "Entry Date",
        sortingFn: (rowA, rowB) => {
          const a = new Date(rowA.original.creationTime).getTime();
          const b = new Date(rowB.original.creationTime).getTime();
          return b - a;
        },
        cell: (props) => format(new Date(props.getValue()), "do MMM yyy"),
      }),
      // columnHelper.display({
      //   header: "Redeemed Prizes",
      //   id: "rewards",
      //   cell: (props) => (
      //     <Stack direction="row" gap={0.5} flexWrap="wrap">
      //       {props.row.original.rewardRedemptions.map((redemption) => (
      //         <Chip key={redemption.reward} size="sm">
      //           {rewardsByUuid[redemption.reward]?.name}
      //         </Chip>
      //       ))}
      //     </Stack>
      //   ),
      // }),
      ...(challenge?.tiers.map((tier, index) =>
        columnHelper.display({
          header: `Tier ${index + 1}`,
          id: `tier-${tier.uuid}`,
          cell: (props) => {
            const allRewardsRedeemed = tier.rewards.every((reward) =>
              props.row.original.rewardRedemptions.some((r) => r.reward === reward.uuid),
            );
            return (
              <Box>
                {props.row.original.tiersReached.includes(tier.uuid) ? (
                  <Chip
                    size="sm"
                    color={allRewardsRedeemed ? "neutral" : "danger"}
                    variant="soft"
                    startDecorator={allRewardsRedeemed ? <Check /> : null}
                    sx={{ overflow: "hidden", width: "100%" }}
                  >
                    {allRewardsRedeemed ? tier.rewards.map((x) => x.name).join(", ") : "Unclaimed"}
                  </Chip>
                ) : null}
              </Box>
            );
          },
        }),
      ) || []),
    ],
    [challenge],
  );

  const table = useReactTable<ApiChallengeEntry>({
    columns,
    data: filteredEntries || [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    initialState: {
      sorting: [{ id: "creationTime", desc: true }],
    },
  });

  return (
    <Stack direction="row" overflow="hidden">
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <FiltersBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        <Box overflow="auto" flex={1}>
          <MuiTable
            borderAxis="bothBetween"
            hoverRow
            sx={{
              td: { position: "relative" },
              "th, td": {
                paddingX: 1,
                width: 120,
              },
              "thead th": {
                height: 30,
              },
              "th:nth-child(1), td:nth-child(1)": {
                width: 250,
              },
              "th:nth-child(2), td:nth-child(2)": {
                width: 90,
              },
              th: {
                backgroundColor: "neutral.100",
              },
              "--TableRow-hoverBackground": (theme) => theme.vars.palette.rowHoverBackground,
              thead: {
                position: "sticky",
                top: 0,
                zIndex: 1,
              },
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return <TableHeader key={header.id} header={header} />;
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick(row.original.uuid)}
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      row.original.uuid === activeEntryUuid
                        ? "var(--joy-palette-primary-softBg)"
                        : undefined,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </MuiTable>
        </Box>
      </Stack>
      <Outlet />
    </Stack>
  );
}
